/*
 * File: UIStore.js
 * Project: mp-mobile-app
 *
 * Copyright (c) 2019 MacPractice, Inc. All rights reserved.
 *
 * Description: Observable store to set values for the Schedule UI to observe
 */
import { addDays, subDays } from "date-fns";
import { format } from "date-fns";
import { types } from "mobx-state-tree";

const dateLocale = "en-Us";

const prevDate = date => {
  return subDays(date, 1);
};
const nextDate = date => {
  return addDays(date, 1);
};

const Store = types
  .model("ApptUiStore", {
    date: types.maybe(types.Date),
    prevDateString: types.maybe(types.string),
    nextDateString: types.maybe(types.string),
    dateString: "Today",
    showCalendarDialog: false,
    showFiltersDialog: false,
    showFormDialog: false,
    showDayView: false,
    showPatientSelection: false,
    resourceId: 0,
    prefsLoaded: false
  })
  .actions(self => ({
    afterCreate: () => {
      self.setDate(new Date())
    },

    setDate: date => {
      self.date = date;
      self.prevDateString = format(prevDate(self.date), "MM/dd/yyyy");
      self.nextDateString = format(nextDate(self.date), "MM/dd/yyyy");
      if (self.isToday(date)) {
        return (self.dateString = "Today");
      }
      return (self.dateString = self._createDateString(self.date));
    },
    /**
     * It preserves the selected resource Id to retain it for later use.
     */
    setResourceId: resourceId => {
      self.resourceId = resourceId;
    },

    fetchShowDayViewPref: preferencesStore => {
      if (self.prefsLoaded === false) {
        const prefValue = preferencesStore.get("defaultSchedulingView");
        self.showDayView = prefValue === "dayView";
        self.prefsLoaded = true;
      }
    },

    toggleDayView: preferencesStore => {
      self.showDayView = !self.showDayView;
      preferencesStore.apply(
        "defaultSchedulingView",
        self.showDayView ? "dayView" : "listView"
      );
    },

    togglePatientSelection: () => {
      self.showPatientSelection = !self.showPatientSelection;
    },

    toggleCalendarDialog: () => {
      self.showCalendarDialog = !self.showCalendarDialog;
    },

    toggleFormDialog: () => {
      self.showFormDialog = !self.showFormDialog;
    },

    toggleFiltersDialog: () => {
      self.showFiltersDialog = !self.showFiltersDialog;
    },

    prev: () => {
      self.setDate(prevDate(self.date));
    },

    next: () => {
      self.setDate(nextDate(self.date));
    }
  }))
  .views(() => ({
    _createDateString: date => {
      let options = {
        weekday: "short",
        year: "numeric",
        month: "short",
        day: "numeric"
      };
      return date.toLocaleDateString(dateLocale, options);
    },
    isToday: date => {
      return (
        date.toLocaleDateString(dateLocale) ===
        new Date().toLocaleDateString(dateLocale)
      );
    }
  }));

export default Store;
